const CART_DRAWER_SECTION_RENDERS = [
  { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
  { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
];

function cartDrawerGetSectionInnerHTML(html, selector) {
  const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return el ? el.innerHTML : '';
}

function replaceCartDrawerSectionsFromResponse(parsedState) {
  if (!parsedState.sections) return;
  CART_DRAWER_SECTION_RENDERS.forEach((section) => {
    const html = parsedState.sections[section.section];
    if (!html) return;
    const root = document.getElementById(section.id);
    if (!root) return;
    const elementToReplace = root.querySelector(section.selector) || root;
    const inner = cartDrawerGetSectionInnerHTML(html, section.selector);
    if (inner) elementToReplace.innerHTML = inner;
  });
  const cartDrawerEl = document.querySelector('cart-drawer');
  if (cartDrawerEl && typeof parsedState.item_count === 'number') {
    cartDrawerEl.classList.toggle('is-empty', parsedState.item_count === 0);
  }
}

/**
 * Re-fetch rendered sections like a full navigation. cart/update.js bundled sections can omit
 * restored automatic / line-item discounts until storefront render catches up (refresh works).
 */
function refetchCartDrawerSectionsFromShop() {
  const sectionIds = CART_DRAWER_SECTION_RENDERS.map((s) => s.section).join(',');
  const u = new URL(window.location.href);
  u.searchParams.set('sections', sectionIds);

  return fetch(u.toString())
    .then((r) => r.json())
    .then((sectionsJson) =>
      fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`).then((cartRes) =>
        cartRes.json().then((cart) => {
          replaceCartDrawerSectionsFromResponse({
            sections: sectionsJson,
            item_count: cart.item_count,
          });
          syncCartDrawerDiscountBadgesFromParsedCart(cart);
        })
      )
    );
}

const EVA_CART_ATTR_DISCOUNT_CODE = 'discountCode';

/**
 * Saves the discount field exactly as sent to cart/update.js (same string as `discount` in the POST body).
 * Does not read discount_codes or applicability. Merges with existing cart attributes.
 */
function syncCartDiscountCodeAttributeIfNeeded(parsedCart, valueAsSent) {
  if (!parsedCart || !window.routes || !window.routes.cart_update_url) return Promise.resolve();

  const next = valueAsSent === '' ? '' : String(valueAsSent);

  const existing = parsedCart.attributes && typeof parsedCart.attributes === 'object' ? parsedCart.attributes : {};
  const prev = existing[EVA_CART_ATTR_DISCOUNT_CODE] != null ? String(existing[EVA_CART_ATTR_DISCOUNT_CODE]) : '';

  if (prev === next) {
    return Promise.resolve();
  }

  const attributes = { ...existing, [EVA_CART_ATTR_DISCOUNT_CODE]: next };
  const body = JSON.stringify({ attributes });

  return fetch(window.routes.cart_update_url, { ...fetchConfig(), body }).catch(() => {});
}

function wasCartDiscountApplied(cart, requestedCode) {
  if (!requestedCode || !requestedCode.trim()) return true;
  const norm = requestedCode.trim().toUpperCase();
  const codes = cart.discount_codes || [];
  for (let i = 0; i < codes.length; i++) {
    const d = codes[i];
    const c = (d.code || '').toUpperCase();
    if (c === norm) {
      return d.applicable !== false;
    }
  }
  const apps = cart.cart_level_discount_applications || [];
  return apps.some((a) => (a.title || '').toUpperCase() === norm);
}

function escapeHtmlDiscount(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function discountBadgeAriaLabel(title) {
  const tpl = window.cartStrings && window.cartStrings.discountBadgeRemoveTemplate;
  if (tpl && tpl.includes('[[CODE]]')) return tpl.replace('[[CODE]]', String(title || ''));
  return `${(window.cartStrings && window.cartStrings.discountRemove) || 'Remove'} ${title || ''}`;
}

/**
 * Checkout shows code discounts as chips; Liquid sometimes omits them (cart_level empty, etc.).
 * Ensures badges exist from the same JSON returned by cart/update.js.
 */
function syncCartDrawerDiscountBadgesFromParsedCart(parsed) {
  const wrap = document.querySelector('cart-drawer [data-cart-discount]');
  if (!wrap || !parsed) return;

  const apps = parsed.discount_applications || parsed.cart_level_discount_applications || [];
  const visible = apps.filter((a) => (a.target_type || '') !== 'line_item');

  const isCodeLike = (a) => {
    const t = (a.type || '').toLowerCase();
    return t === 'discount_code' || t === 'manual' || (t.includes('code') && t !== 'automatic');
  };

  let displayRows = visible.filter(isCodeLike);

  const parsedCodes = (parsed.discount_codes || []).map((d) => (typeof d === 'string' ? d : d.code)).filter(Boolean);
  if (displayRows.length === 0 && parsedCodes.length > 0) {
    const totalDisc = parsed.total_discount || 0;
    displayRows = parsedCodes.map((code) => ({
      title: code,
      total_allocated_amount: parsedCodes.length === 1 ? totalDisc : 0,
      type: 'discount_code',
    }));
  }

  if (displayRows.length === 0 && visible.length > 0) {
    displayRows = visible;
  }

  if (displayRows.length === 0) return;

  wrap.querySelector('#CartDrawer-DiscountAppliedHeading')?.remove();
  wrap.querySelector('.cart-drawer__discount-badges')?.remove();

  const label = document.createElement('p');
  label.className = 'cart-drawer__discount-applied-label';
  label.id = 'CartDrawer-DiscountAppliedHeading';
  label.textContent = (window.cartStrings && window.cartStrings.discountAppliedLabel) || '';

  const ul = document.createElement('ul');
  ul.className = 'cart-drawer__discount-badges';
  ul.setAttribute('role', 'list');
  ul.setAttribute('aria-labelledby', 'CartDrawer-DiscountAppliedHeading');

  let codeIdx = 0;
  const discountIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="icon icon-discount" viewBox="0 0 12 12" width="12" height="12"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" clip-rule="evenodd"/></svg>`;

  displayRows.forEach((discount) => {
    const removable = isCodeLike(discount);
    const li = document.createElement('li');
    li.className = `cart-drawer__discount-badge${removable ? ' cart-drawer__discount-badge--removable' : ''}`;

    const amt = discount.total_allocated_amount;
    let amountHtml = '';
    if (amt != null && amt !== '' && typeof window.formatMoney === 'function') {
      const n = typeof amt === 'string' ? parseInt(amt, 10) : amt;
      if (!Number.isNaN(n)) amountHtml = `<span class="cart-drawer__discount-badge-amount">−$${window.formatMoney(n)}</span>`;
    }

    let removeHtml = '';
    if (removable) {
      const idx = codeIdx;
      codeIdx += 1;
      removeHtml = `<button type="button" class="cart-drawer__discount-badge-remove" data-cart-discount-badge-remove data-cart-discount-code-index="${idx}" aria-label="${escapeHtmlDiscount(discountBadgeAriaLabel(discount.title))}"><span class="visually-hidden">${escapeHtmlDiscount((window.cartStrings && window.cartStrings.discountRemove) || '')}</span><span aria-hidden="true" class="cart-drawer__discount-badge-x">×</span></button>`;
    }

    li.innerHTML = `${discountIconSvg}<span class="cart-drawer__discount-badge-text"><span class="cart-drawer__discount-badge-title">${escapeHtmlDiscount(discount.title)}</span>${amountHtml}</span>${removeHtml}`;
    ul.appendChild(li);
  });

  const errEl = wrap.querySelector('#CartDrawer-DiscountError');
  if (errEl) {
    wrap.insertBefore(label, errEl);
    wrap.insertBefore(ul, errEl);
  } else {
    wrap.appendChild(label);
    wrap.appendChild(ul);
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.onDiscountInteraction.bind(this));
    this.addEventListener('keydown', this.onDiscountKeydown.bind(this));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        const errorsEl = this.querySelector('#CartDrawer-CartErrors');
        if (errorsEl) {
          errorsEl.textContent = event.message || (window.cartStrings && window.cartStrings.error) || 'There was an error updating your cart.';
          errorsEl.classList.add('cart-drawer__error--visible');
        }
        this.open();
      });
    }
  }

  disconnectedCallback() {
    if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
  }

  connectedCallback() {
    this.hydrateDiscountBadgesIfMissingFromCart();
  }

  /**
   * After full page load, Liquid often omits cart.discount_applications while /cart.js still has codes.
   * If the drawer has no badges but the Ajax cart shows discount codes, build badges like post-update JS.
   */
  hydrateDiscountBadgesIfMissingFromCart() {
    queueMicrotask(() => {
      const wrap = this.querySelector('[data-cart-discount]');
      if (!wrap || wrap.querySelector('.cart-drawer__discount-badges')) return;
      const cartBase = (window.routes && window.routes.cart_url) || '/cart';
      fetch(`${cartBase}.js`)
        .then((r) => r.json())
        .then((cart) => {
          if (!cart || !cart.item_count) return;
          const codes = (cart.discount_codes || []).filter((d) => d && d.applicable !== false && d.code);
          if (codes.length === 0) return;
          syncCartDrawerDiscountBadgesFromParsedCart(cart);
        })
        .catch(() => {});
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('.cart-icon-bubble');

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.querySelector('.drawer').classList.remove('upsell-closed');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  onDiscountKeydown(event) {
    if (event.target.id !== 'CartDrawer-Discount' || event.code !== 'Enter') return;
    event.preventDefault();
    this.submitCartDiscount(event.target.value);
  }

  onDiscountInteraction(event) {
    if (event.target.closest('[data-cart-discount-apply]')) {
      event.preventDefault();
      const wrap = this.querySelector('[data-cart-discount]');
      const input = wrap && wrap.querySelector('#CartDrawer-Discount');
      if (input) this.submitCartDiscount(input.value);
      return;
    }
    const badgeRemove = event.target.closest('[data-cart-discount-badge-remove]');
    if (badgeRemove) {
      event.preventDefault();
      const raw = badgeRemove.getAttribute('data-cart-discount-code-index');
      const idx = raw === null ? NaN : parseInt(raw, 10);
      if (!Number.isNaN(idx)) this.removeDiscountAtCodeIndex(idx);
    }
  }

  removeDiscountAtCodeIndex(codeIndex) {
    fetch(`${window.routes.cart_url}.js`)
      .then((r) => r.json())
      .then((cart) => {
        const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
        if (codeIndex < 0 || codeIndex >= codes.length) {
          this.submitCartDiscount('', { skipAppliedCheck: true, fromDiscountBadgeRemove: true });
          return;
        }
        const next = codes.filter((_, i) => i !== codeIndex);
        this.submitCartDiscount(next.join(','), { skipAppliedCheck: true, fromDiscountBadgeRemove: true });
      })
      .catch(() => {
        this.submitCartDiscount('', { skipAppliedCheck: true, fromDiscountBadgeRemove: true });
      });
  }

  setDiscountInputAriaInvalid(invalid) {
    const input = this.querySelector('#CartDrawer-Discount');
    if (input) input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
  }

  submitCartDiscount(discountValue, options = {}) {
    const errEl = this.querySelector('#CartDrawer-DiscountError');
    const wrap = this.querySelector('[data-cart-discount]');
    if (!wrap || !window.routes || !window.routes.cart_update_url) return;

    const requestedCode = String(discountValue).trim();
    const isRemove = discountValue === '';
    const skipAppliedCheck = options.skipAppliedCheck === true;
    const fromDiscountBadgeRemove = options.fromDiscountBadgeRemove === true;

    if (!isRemove && !requestedCode) {
      if (errEl) {
        errEl.textContent = (window.cartStrings && window.cartStrings.discountCodeEmpty) || '';
        errEl.hidden = false;
      }
      this.setDiscountInputAriaInvalid(true);
      const input = wrap.querySelector('#CartDrawer-Discount');
      if (input) input.focus();
      return;
    }

    if (errEl) {
      errEl.textContent = '';
      errEl.hidden = true;
    }
    this.setDiscountInputAriaInvalid(false);

    const discountControls = wrap.querySelectorAll('button, .cart-drawer__discount-input');
    discountControls.forEach((el) => {
      if (el) el.disabled = true;
    });

    const isApplyFromField = !isRemove && requestedCode && !fromDiscountBadgeRemove;

    const discountBody = JSON.stringify({
      discount: isRemove ? '' : requestedCode,
      sections: CART_DRAWER_SECTION_RENDERS.map((s) => s.section),
      sections_url: window.location.pathname,
    });

    const handleDiscountResponse = ({ ok, text }) => {
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        if (errEl) {
          errEl.textContent =
            (window.cartStrings && window.cartStrings.discountError) ||
            (window.cartStrings && window.cartStrings.error) ||
            '';
          errEl.hidden = false;
        }
        this.setDiscountInputAriaInvalid(true);
        return;
      }

      if (!ok && (parsed.status || parsed.message || parsed.description)) {
        if (errEl) {
          errEl.textContent =
            parsed.description ||
            parsed.message ||
            (window.cartStrings && window.cartStrings.discountError) ||
            '';
          errEl.hidden = false;
        }
        this.setDiscountInputAriaInvalid(true);
        return;
      }

      if (parsed.errors || (parsed.status && Number(parsed.status) >= 400)) {
        if (errEl) {
          errEl.textContent =
            parsed.description ||
            parsed.message ||
            (window.cartStrings && window.cartStrings.discountError) ||
            '';
          errEl.hidden = false;
        }
        this.setDiscountInputAriaInvalid(true);
        return;
      }

      const discountCodeAttrValue =
        fromDiscountBadgeRemove || isRemove ? '' : requestedCode;

      const runSuccessUi = () => {
        if (
          !skipAppliedCheck &&
          !isRemove &&
          requestedCode &&
          !wasCartDiscountApplied(parsed, requestedCode)
        ) {
          if (errEl) {
            errEl.textContent = (window.cartStrings && window.cartStrings.discountError) || '';
            errEl.hidden = false;
          }
          this.setDiscountInputAriaInvalid(true);
          return Promise.resolve();
        }

        replaceCartDrawerSectionsFromResponse(parsed);
        syncCartDrawerDiscountBadgesFromParsedCart(parsed);

        const publishAndScroll = () => {
          if (!isRemove) {
            const clearedInput = this.querySelector('#CartDrawer-Discount');
            if (clearedInput) clearedInput.value = '';
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount' });
          }

          if (!skipAppliedCheck && !isRemove && requestedCode) {
            requestAnimationFrame(() => {
              const badges = this.querySelector('.cart-drawer__discount-badges');
              if (badges) badges.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
          }
        };

        if (isRemove || fromDiscountBadgeRemove) {
          return refetchCartDrawerSectionsFromShop()
            .then(publishAndScroll)
            .catch(publishAndScroll);
        }

        publishAndScroll();
        return Promise.resolve();
      };

      if (isApplyFromField) {
        return Promise.resolve().then(runSuccessUi);
      }
      return syncCartDiscountCodeAttributeIfNeeded(parsed, discountCodeAttrValue).then(runSuccessUi);
    };

    const cartJsUrl = `${(window.routes && window.routes.cart_url) || '/cart'}.js`;
    let pipeline = Promise.resolve();

    if (isApplyFromField) {
      pipeline = fetch(cartJsUrl)
        .then((r) => r.json())
        .then((cart) =>
          syncCartDiscountCodeAttributeIfNeeded({ attributes: cart.attributes || {} }, requestedCode)
        )
        .catch(() => {})
        .then(
          () =>
            new Promise((resolve) => {
              setTimeout(resolve, 2000);
            })
        );
    }

    pipeline
      .then(() =>
        fetch(window.routes.cart_update_url, { ...fetchConfig(), body: discountBody }).then((response) =>
          response.text().then((text) => ({ ok: response.ok, text }))
        )
      )
      .then(handleDiscountResponse)
      .catch(() => {
        if (errEl) {
          errEl.textContent =
            (window.cartStrings && window.cartStrings.discountError) ||
            (window.cartStrings && window.cartStrings.error) ||
            '';
          errEl.hidden = false;
        }
        this.setDiscountInputAriaInvalid(true);
      })
      .finally(() => {
        const wrapAfter = this.querySelector('[data-cart-discount]');
        if (!wrapAfter) return;
        wrapAfter.querySelectorAll('button, .cart-drawer__discount-input').forEach((el) => {
          if (el) el.disabled = false;
        });
      });
  }

  renderContents(parsedState) {
    const errorsEl = this.querySelector('#CartDrawer-CartErrors');
    if (errorsEl) {
      errorsEl.textContent = '';
      errorsEl.classList.remove('cart-drawer__error--visible');
    }
    // The `is-empty` class lives on the <cart-drawer> ELEMENT (see
    // snippets/cart-drawer.liquid line 17), and CSS hides the cart contents
    // while it's set (cart-drawer.is-empty .drawer__inner / .drawer__header in
    // component-cart-drawer.css). renderContents only swaps #CartDrawer's inner
    // HTML — not the element's class — and the previous line cleared the class
    // off `.drawer__inner` (the wrong node), so after an AJAX add to a
    // previously-empty cart the class lingered on <cart-drawer> and the freshly
    // rendered items stayed hidden → a blank drawer. Toggle it on the element
    // from the response's item_count (add.js omits it → a successful add is
    // always non-empty, so clear it).
    const cartItemCount = typeof parsedState.item_count === 'number' ? parsedState.item_count : null;
    this.classList.toggle('is-empty', cartItemCount === 0);
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });


    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      if(document.querySelector('.drawer__upsell-close')) {
        document.querySelector('.drawer__upsell-close').addEventListener('click', () => {
          document.querySelector('.drawer__upsell').classList.add('visually-hidden');
          document.querySelector('.drawer').classList.add('upsell-closed');

        });
      }
    },0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /**
   * CartItems.onCartUpdate refetches `main-cart-items` (full cart page) and pastes it here.
   * Drawer markup differs, so that swaps in the wrong DOM and breaks layout/CSS.
   * The drawer is refreshed by cart/change + bundled `cart-drawer` sections, renderContents,
   * or replaceCartDrawerSectionsFromResponse — never from main-cart-items.
   */
  onCartUpdate() {}

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
